import React from 'react';
import Select, { StylesConfig } from 'react-select';
import {
  EMPTY_FILTERS,
  QueueFilters,
  QueueSort,
  UNASSIGNED,
  activeFilterCount
} from '../../shared/ticket-queue.util';

const FIELD =
  'h-[40px] w-full rounded-[10px] border border-gray-200 bg-neutral-0 px-[12px] text-[13px] text-neutral-500 hover:border-neutral-500 focus:border-neutral-500 outline-none transition-colors';
const LABEL = 'flex flex-col gap-[6px] text-[12px] font-gilroy-medium text-neutral-300';

interface Option {
  value: string;
  label: string;
}

const selectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '10px',
    borderColor: state.isFocused ? '#292a2b' : '#e6e8ec',
    boxShadow: 'none',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    '&:hover': { borderColor: '#292a2b' }
  }),
  valueContainer: (base) => ({ ...base, padding: '0 12px' }),
  singleValue: (base) => ({ ...base, fontSize: '13px', color: '#292a2b' }),
  placeholder: (base) => ({ ...base, fontSize: '13px' }),
  input: (base) => ({ ...base, fontSize: '13px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 10px 0 4px', color: '#707071' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    marginTop: '6px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #e6e8ec',
    boxShadow: '0px 2px 6px 2px rgba(0,0,0,0.08)'
  }),
  menuList: (base) => ({ ...base, padding: '6px' }),
  option: (base, state) => ({
    ...base,
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: state.isSelected ? 'Gilroy-Medium' : 'Gilroy-Regular',
    color: '#292a2b',
    backgroundColor: state.isSelected ? '#f8f4f1' : state.isFocused ? '#faf8f6' : 'transparent',
    cursor: 'pointer'
  })
};

const toOptions = (values: string[]): Option[] => values.map((v) => ({ value: v, label: v }));

const FilterSelect: React.FC<{
  label: string;
  value: string;
  options: Option[];
  searchable?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, options, searchable, onChange }) => (
  <div className={LABEL}>
    {label}
    <Select<Option, false>
      aria-label={label}
      value={options.find((o) => o.value === value) ?? null}
      options={options}
      isSearchable={!!searchable}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      styles={selectStyles}
      onChange={(selected) => onChange(selected?.value ?? '')}
    />
  </div>
);

interface Props {
  filters: QueueFilters;
  sort: QueueSort;
  options: { teams: string[]; members: string[]; categories: string[] };
  onChange: (next: QueueFilters) => void;
  onSort: (sort: QueueSort) => void;
}

/** The filter bar over the ticket list (CTO, 2026-10-13). Narrows the queue on screen. */
export const QueueFiltersBar: React.FC<Props> = ({ filters, sort, options, onChange, onSort }) => {
  const set = <K extends keyof QueueFilters>(key: K, value: QueueFilters[K]) =>
    onChange({ ...filters, [key]: value });
  const count = activeFilterCount(filters);
  return (
    <div className="grid grid-cols-2 gap-[12px] md:grid-cols-4 xl:grid-cols-8">
      <label className={`${LABEL} col-span-2`}>
        Search
        <input
          className={FIELD}
          placeholder="Number, title, team, reservation"
          value={filters.text}
          onChange={(e) => set('text', e.target.value)}
        />
      </label>
      <FilterSelect
        label="Team"
        value={filters.team}
        options={[{ value: '', label: 'Any' }, ...toOptions(options.teams)]}
        searchable
        onChange={(v) => set('team', v)}
      />
      <FilterSelect
        label="Team member"
        value={filters.member}
        options={[
          { value: '', label: 'Anyone' },
          { value: UNASSIGNED, label: 'Unassigned' },
          ...toOptions(options.members)
        ]}
        searchable
        onChange={(v) => set('member', v)}
      />
      <FilterSelect
        label="Priority"
        value={filters.priority}
        options={[{ value: '', label: 'Any' }, ...toOptions(['P1', 'P2', 'P3', 'P4'])]}
        onChange={(v) => set('priority', v)}
      />
      <FilterSelect
        label="State"
        value={filters.state}
        options={[
          { value: '', label: 'Any' },
          { value: 'open', label: 'Open' },
          { value: 'closed', label: 'Closed' }
        ]}
        onChange={(v) => set('state', v as QueueFilters['state'])}
      />
      <FilterSelect
        label="Category"
        value={filters.category}
        options={[{ value: '', label: 'Any' }, ...toOptions(options.categories)]}
        searchable
        onChange={(v) => set('category', v)}
      />
      <FilterSelect
        label="SLA"
        value={filters.sla}
        options={[
          { value: '', label: 'Any' },
          { value: 'breached', label: 'Breached' },
          { value: 'at_risk', label: 'Due soon' },
          { value: 'on_track', label: 'On track' },
          { value: 'met', label: 'Met' },
          { value: 'none', label: 'No SLA' }
        ]}
        onChange={(v) => set('sla', v as QueueFilters['sla'])}
      />
      <FilterSelect
        label="Sort by"
        value={sort}
        options={[
          { value: 'updated', label: 'Last updated' },
          { value: 'created', label: 'Newest' },
          { value: 'priority', label: 'Priority' },
          { value: 'due', label: 'SLA due first' }
        ]}
        onChange={(v) => onSort(v as QueueSort)}
      />
      <label className={LABEL}>
        Created from
        <input
          type="date"
          className={FIELD}
          value={filters.createdFrom}
          onChange={(e) => set('createdFrom', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Created to
        <input
          type="date"
          className={FIELD}
          value={filters.createdTo}
          onChange={(e) => set('createdTo', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Updated from
        <input
          type="date"
          className={FIELD}
          value={filters.updatedFrom}
          onChange={(e) => set('updatedFrom', e.target.value)}
        />
      </label>
      <label className={LABEL}>
        Updated to
        <input
          type="date"
          className={FIELD}
          value={filters.updatedTo}
          onChange={(e) => set('updatedTo', e.target.value)}
        />
      </label>
      <label className="flex h-[40px] items-center gap-[8px] self-end text-[13px] text-neutral-500 cursor-pointer">
        <input
          type="checkbox"
          checked={filters.emergencyOnly}
          onChange={(e) => set('emergencyOnly', e.target.checked)}
        />
        Emergencies only
      </label>
      <div className="flex h-[40px] items-center self-end">
        {count || filters.text ? (
          <button
            type="button"
            className="text-[13px] font-gilroy-medium text-neutral-500 underline underline-offset-[3px] cursor-pointer"
            onClick={() => onChange(EMPTY_FILTERS)}
          >
            Clear filters{count ? ` (${count})` : ''}
          </button>
        ) : null}
      </div>
    </div>
  );
};
